import { AES, enc, MD5 } from "crypto-js";
import * as MaterialDesignIcons from "react-icons/md";

import IFamily, { EAge } from "../models/IFamily";
import {
    AAPMediaToolkitPageData,
    EAAPRequestTypes,
    IAAPServerPlan,
    IAAPServerPlanPayloadDelete,
    IAAPServerPlanPayloadEMail,
    IAAPServerPlanPayloadGetGuest,
    IAAPServerPlanPayloadInsert,
    IAAPServerPlanWritable,
} from "../models/IMediaToolkit";
import { GetDataFromServer } from "./dataService";
import { differenceInYears } from 'date-fns';
import { IconType } from "react-icons/lib";

export default class AAPService {
    public static GetToolInfo = async (): Promise<AAPMediaToolkitPageData> => {
        return await GetDataFromServer<AAPMediaToolkitPageData>({ type: EAAPRequestTypes.getfullplandata });
    };

    public static GetSharedPlan = async (planguid: string, decryptionkey: string): Promise<IAAPServerPlan> => {
        const payload: IAAPServerPlanPayloadGetGuest = {
            type: EAAPRequestTypes.getsharedplan,
            plan_guid: planguid,
        };
        const sharedPlan = await GetDataFromServer<IAAPServerPlanWritable>(payload);
        sharedPlan.json_raw = AAPService.Decrypt(sharedPlan.json_encrypted, decryptionkey) || '';

        return AAPService.FixPlanInfoFromServer(sharedPlan);
    };

    public static DeletePlan = async (planid: number): Promise<boolean> => {
        const payload: IAAPServerPlanPayloadDelete = {
            type: EAAPRequestTypes.deleteplan,
            id: planid
        }

        const deletion = await GetDataFromServer<any>(payload);

        console.log('deletion response', deletion);

        return deletion;
    }

    public static GetMyPlans = async (): Promise<IAAPServerPlan[]> => {
        const plans = await GetDataFromServer<IAAPServerPlanWritable[]>({ type: EAAPRequestTypes.getmyplans });

        return plans.map((plan) => AAPService.FixPlanInfoFromServer(plan));
    };

    public static SaveMyPlan = async (planData: IAAPServerPlan, existing: boolean): Promise<boolean> => {
        const payload: IAAPServerPlanPayloadInsert = {
            type: existing ? EAAPRequestTypes.updateplan : EAAPRequestTypes.insertplan,
            planInfo: AAPService.FixPlanInfoForWrite(planData),
        };

        try {
            await GetDataFromServer<any>(payload);
            return true;
        } catch {
            return false;
        }
    };

    public static EmailPlan = async (mediaPlanUrl: string, toAddress: string): Promise<{ emailSuccess: boolean, emailError: any }> => {
        const payload: IAAPServerPlanPayloadEMail = {
            type: EAAPRequestTypes.emailplan,
            mediaPlanUrl,
            toAddress
        }

        try {
            const sendAttempt = await GetDataFromServer<any>(payload);
            sendAttempt.emailError = decodeURIComponent(sendAttempt.emailError || '');

            if(!sendAttempt.emailSuccess) {
                throw sendAttempt;
            }

            return sendAttempt;
        } catch(er) {
            throw er;
        }
    }

    public static FixPlanInfoForWrite = (planData: IAAPServerPlan): IAAPServerPlanWritable => {
        return {
            json_raw: JSON.stringify(planData.family),
            json_encrypted: planData.json_encrypted,
            plan_guid: planData.plan_guid,
            is_public: planData.is_public,
            hide_child_names: planData.hide_child_names,
            hide_last_name: planData.hide_last_name,
            plan_password_hash: planData.plan_password_hash,
            id: planData.id,
        };
    };

    public static FixPlanInfoFromServer = (planData: IAAPServerPlanWritable): IAAPServerPlan => {
        const parsedFamily = JSON.parse(planData.json_raw);

        return {
            family: parsedFamily.family ? parsedFamily.family : parsedFamily,
            json_encrypted: planData.json_encrypted,
            plan_guid: planData.plan_guid,
            is_public: planData.is_public,
            hide_child_names: planData.hide_child_names,
            hide_last_name: planData.hide_last_name,
            plan_password_hash: planData.plan_password_hash,
            id: planData.id,
        };
    };
    
    public static Encrypt = (familyObj: IFamily, secret: string): string => {
        let curPlanStr = JSON.stringify(familyObj);
        const iv = enc.Hex.parse("00000000000000000000000000000000");
        return AES.encrypt(curPlanStr, MD5(secret), { iv }).toString();
    }

    public static Decrypt = (encrypted: string, secret: string): string | null => {
        let decrypted: string | null = null;
        const iv = enc.Hex.parse("00000000000000000000000000000000");

        try {
            decrypted = AES.decrypt(encrypted, MD5(secret), { iv }).toString(enc.Utf8)
        } catch (er) {
            decrypted = null;
        }

        return decrypted
    }
    
    public static NormalizeAge = (dob: string) => {

        const dobNormal = (dob.split('(').pop() || '').split(')')[0];
        const dobDate = new Date(parseInt(dobNormal));

        const now = new Date();
        const childAge = Math.floor(differenceInYears(now, dobDate));

        return AAPService.GetAgeString(childAge);
    }

    public static GetAgeString = function (age: number) {
        if (age <= 2) {
            return EAge["0-24 Months"];
        }
        if (age <= 5) {
            return EAge["2-5 Years"];
        }
        if (age <= 12) {
            return EAge["6-12 Years"];
        }
        if (age <= 18) {
            return EAge["13-18 Years"];
        }
        if (age > 18) {
            return EAge["Adult"];
        }

        return null;
    }

    public static ConvertIconToMdIconString = (iconStr: string) => {
        // fix the icon to be inline with our naming convention
        // from SPO it will be volunteer_activism, our icon kit
        // needs to be MdVolunteerActivism

        if(iconStr.indexOf('Md') === 0) {
            // assume its already in the proper format
            return iconStr;
        }
        return `Md${iconStr
            .toLowerCase()
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join("")}`;
    }

    public static GetMaterialIcon = (iconStr: string): IconType => {
        const iconString = AAPService.ConvertIconToMdIconString(iconStr);

        let iconObj = MaterialDesignIcons[iconString as keyof typeof MaterialDesignIcons];

        if (!iconObj) {
            // fallback if icon doesn't exist above
            iconObj = MaterialDesignIcons["MdErrorOutline"];
        }

        return iconObj;
    }
}
